import { memo } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';

import DebouncedColorInput from './DebouncedColorInput';
import { DEFAULT_CUSTOM_PRICE_LABEL } from './matrixClassification';
import {
	Section,
	SectionTitle,
	EditorGrid,
	EditorColumn,
	ClassItem,
	BandTag,
	MatrixTableWrapper,
	MatrixTable,
	PriceInput,
	SummaryCard,
	SummaryRow,
	SummaryLabel,
	SummaryValue,
	SummaryDetailText,
	EditableLabelInput,
	TotalAreaRow,
	TotalRow,
	ColorDot,
} from './StyledMatrixFilterComponents';

const priceInputStyle = { width: '65px' };
const EMPTY_CUSTOM_PRICE_FIELDS = [];

/**
 * MatrixEditor — the Matrix (2D) filter editor panel.
 *
 * Rendered inside the Offcanvas drawer when filter.type !== 'simple'.
 * Memoized to avoid re-renders when only the simple-specific state changes.
 *
 * @param {Object} props
 * @param {Object} props.filter - Current matrix filter configuration
 * @param {Function} props.updateFilter - Stable callback to merge updates into filter state
 * @param {Object} props.classificationResults - Per-class classification results (for counts display)
 * @param {Object} props.currency - Resolved currency object ({ id, symbol, label })
 * @param {Array} props.currencies - Full list of available currencies for dropdown
 * @param {string} props.bandPropertyLabel - Display label for the selected band property
 * @param {string} props.thresholdPropertyLabel - Display label for the selected threshold property
 * @param {{ min: number, max: number, hasData: boolean, coverage: number }} props.bandRange - Data range for band property
 * @param {{ min: number, max: number, hasData: boolean, coverage: number }} props.thresholdRange - Data range for threshold property
 * @param {Array} props.availableProperties - Properties that exist in the hexagon data (for dropdowns)
 * @param {{ hasGaps: boolean, gapsByBand: Array<{bandIndex: number, band: number, min: number, max: number}> }} props.coverageGaps -
 *   Result of getMatrixFilterCoverageGaps, computed once by the parent Mode
 *   component so the same result can be reused in the exported report.
 * @param {string} props.projectNotes - User-entered project notes
 * @param {Function} props.setProjectNotes - Notes state setter
 * @param {boolean} props.isExporting - Whether an export is in progress
 * @param {Function} props.onOpenExportModal - Stable callback to open the export modal
 */
const MatrixEditor = memo(function MatrixEditor({
	filter,
	updateFilter,
	classificationResults,
	currency,
	currencies,
	bandPropertyLabel,
	thresholdPropertyLabel,
	bandRange,
	thresholdRange,
	availableProperties,
	coverageGaps,
	projectNotes,
	setProjectNotes,
	isExporting,
	onOpenExportModal,
}) {
	// Compute cost summary totals (single pass over classes)
	const customPriceFields = filter.customPriceFields || EMPTY_CUSTOM_PRICE_FIELDS;
	let areaTotal = 0;
	let classTotal = 0;
	filter.classes.forEach(cls => {
		const area = classificationResults[cls.id]?.totalArea || 0;
		areaTotal += area;
		classTotal += area * (cls.pricePerM2 || 0);
	});
	const customFieldsTotalCost = customPriceFields.reduce(
		(sum, f) => sum + areaTotal * (f.price || 0),
		0
	);
	const grandTotal = classTotal + customFieldsTotalCost;

	const updateCustomField = (idx, patch) => {
		const updated = customPriceFields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
		updateFilter({ customPriceFields: updated });
	};
	const removeCustomField = idx => {
		updateFilter({ customPriceFields: customPriceFields.filter((_, i) => i !== idx) });
	};
	const addCustomField = () => {
		updateFilter({
			customPriceFields: [
				...customPriceFields,
				{ label: DEFAULT_CUSTOM_PRICE_LABEL, price: 0 },
			],
		});
	};

	return (
		<EditorGrid>
			<EditorColumn>
				{/* Property Selection */}
				<Section>
					<SectionTitle>Properties</SectionTitle>
					<div className="d-flex gap-2">
						<Form.Group className="flex-fill">
							<Form.Label style={{ fontSize: '0.85rem' }}>Band (rows)</Form.Label>
							<Form.Select
								size="sm"
								value={
									availableProperties.some(p => p.id === filter.bandProperty)
										? filter.bandProperty
										: ''
								}
								onChange={e => updateFilter({ bandProperty: e.target.value })}>
								{!availableProperties.some(p => p.id === filter.bandProperty) && (
									<option value="" disabled>
										Select a property…
									</option>
								)}
								{availableProperties.map(p => (
									<option key={p.id} value={p.id}>
										{p.label}
									</option>
								))}
							</Form.Select>
						</Form.Group>
						<Form.Group className="flex-fill">
							<Form.Label style={{ fontSize: '0.85rem' }}>
								Threshold (columns)
							</Form.Label>
							<Form.Select
								size="sm"
								value={
									availableProperties.some(p => p.id === filter.thresholdProperty)
										? filter.thresholdProperty
										: ''
								}
								onChange={e => updateFilter({ thresholdProperty: e.target.value })}>
								{!availableProperties.some(
									p => p.id === filter.thresholdProperty
								) && (
									<option value="" disabled>
										Select a property…
									</option>
								)}
								{availableProperties.map(p => (
									<option key={p.id} value={p.id}>
										{p.label}
									</option>
								))}
							</Form.Select>
						</Form.Group>
						<Form.Group style={{ width: '140px' }}>
							<Form.Label style={{ fontSize: '0.85rem' }}>Currency</Form.Label>
							<Form.Select
								size="sm"
								value={filter.currency || 'UNIT'}
								onChange={e => updateFilter({ currency: e.target.value })}>
								{currencies.map(c => (
									<option key={c.id} value={c.id}>
										{c.symbol ? `${c.symbol} - ` : ''}
										{c.label}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					</div>
					<div className="d-flex gap-2 mt-1" style={{ fontSize: '0.75rem' }}>
						<div
							className={`${bandRange.hasData ? 'text-muted' : 'text-warning'}`}
							style={{ flex: '1 1 0', minWidth: 0 }}>
							{bandRange.hasData ? (
								<>
									Band range: {bandRange.min} – {bandRange.max}{' '}
									{availableProperties.find(p => p.id === filter.bandProperty)
										?.unit || ''}
									{bandRange.coverage < 100 &&
										` (${bandRange.coverage}% coverage)`}
								</>
							) : (
								'⚠ Band: No data available'
							)}
						</div>
						<div
							className={`${thresholdRange.hasData ? 'text-muted' : 'text-warning'}`}
							style={{ flex: '1 1 0', minWidth: 0 }}>
							{thresholdRange.hasData ? (
								<>
									Threshold range: {thresholdRange.min} – {thresholdRange.max}{' '}
									{availableProperties.find(
										p => p.id === filter.thresholdProperty
									)?.unit || ''}
									{thresholdRange.coverage < 100 &&
										` (${thresholdRange.coverage}% coverage)`}
								</>
							) : (
								'⚠ Threshold: No data available'
							)}
						</div>
						{/* Spacer aligns with the action column in the matrix table below */}
						<div style={{ width: '140px', flexShrink: 0 }} />
					</div>
					{coverageGaps?.hasGaps && (
						<div
							className="text-warning mt-1"
							style={{ fontSize: '0.75rem' }}
							title="Features whose threshold value falls in these ranges won't match any class boundary and will be counted as Unclassified, excluded from cost totals.">
							⚠ Boundaries don't reach the full threshold range for band(s){' '}
							{(coverageGaps.gapsByBand ?? []).map((g, idx) => (
								<span key={g.bandIndex}>
									{idx > 0 && ', '}
									{g.band} ({g.min.toLocaleString()} – {g.max.toLocaleString()})
								</span>
							))}{' '}
							— values above the last boundary in those rows will show as Unclassified
						</div>
					)}
				</Section>

				{/* Bands */}
				<Section>
					<SectionTitle>{bandPropertyLabel} - Bands</SectionTitle>
					<div className="d-flex flex-wrap">
						{filter.bands?.map((band, i) => (
							<BandTag key={i}>
								<input
									type="number"
									value={band ?? ''}
									onChange={e => {
										const newBands = [...filter.bands];
										const raw = e.target.value;
										newBands[i] = raw === '' ? null : parseFloat(raw);
										updateFilter({ bands: newBands });
									}}
								/>
								<button
									onClick={() => {
										const newBands = filter.bands.filter((_, idx) => idx !== i);
										const newThresholds = filter.thresholds.filter(
											(_, idx) => idx !== i
										);
										updateFilter({
											bands: newBands,
											thresholds: newThresholds,
										});
									}}>
									×
								</button>
							</BandTag>
						))}
					</div>
					<Button
						variant="outline-secondary"
						size="sm"
						className="mt-2"
						onClick={() => {
							const lastBand = filter.bands[filter.bands.length - 1] || 0;
							const lastThreshold = filter.thresholds[
								filter.thresholds.length - 1
							] || { min: 100, boundaries: [500] };
							updateFilter({
								bands: [...filter.bands, lastBand + 1],
								thresholds: [...filter.thresholds, { ...lastThreshold }],
							});
						}}>
						+ Add band
					</Button>
				</Section>

				{/* Classes */}
				<Section>
					<SectionTitle>Classes & Pricing</SectionTitle>
					<div>
						{filter.classes.map((cls, i) => (
							<ClassItem key={cls.id}>
								<DebouncedColorInput
									value={cls.color}
									opacity={cls.opacity ?? 0.7}
									onChange={newColor => {
										const newClasses = [...filter.classes];
										newClasses[i] = { ...cls, color: newColor };
										updateFilter({ classes: newClasses });
									}}
									onOpacityChange={newOpacity => {
										const newClasses = [...filter.classes];
										newClasses[i] = { ...cls, opacity: newOpacity };
										updateFilter({ classes: newClasses });
									}}
								/>
								<Form.Control
									size="sm"
									value={cls.label}
									style={{ flex: 1 }}
									onChange={e => {
										const newClasses = [...filter.classes];
										newClasses[i] = { ...cls, label: e.target.value };
										updateFilter({ classes: newClasses });
									}}
								/>
								<div className="d-flex align-items-center gap-1">
									<PriceInput
										type="number"
										step="0.01"
										value={cls.pricePerM2 ?? ''}
										placeholder="0"
										title="Price per m²"
										onChange={e => {
											const newClasses = [...filter.classes];
											const raw = e.target.value;
											newClasses[i] = {
												...cls,
												pricePerM2: raw === '' ? 0 : parseFloat(raw),
											};
											updateFilter({ classes: newClasses });
										}}
									/>
									<span className="text-muted" style={{ fontSize: '0.85rem' }}>
										{currency.symbol}/m²
									</span>
								</div>
								<Button
									variant="link"
									className="text-danger p-0"
									onClick={() => {
										if (filter.classes.length <= 1) return;
										const newClasses = filter.classes.filter(
											(_, idx) => idx !== i
										);
										// Boundaries array has length = classes.length - 1
										// When deleting class at index i, we need to remove the boundary
										// that separates it from the previous class (index i-1) or next class
										// For the last class, remove the last boundary; otherwise remove boundary at index i
										const boundaryIndexToRemove = Math.min(
											i,
											filter.thresholds[0]?.boundaries?.length - 1 || 0
										);
										const newThresholds = filter.thresholds.map(t => ({
											...t,
											boundaries: t.boundaries.filter(
												(_, idx) => idx !== boundaryIndexToRemove
											),
										}));
										updateFilter({
											classes: newClasses,
											thresholds: newThresholds,
										});
									}}>
									×
								</Button>
							</ClassItem>
						))}
					</div>
					<Button
						variant="outline-secondary"
						size="sm"
						onClick={() => {
							const newClass = {
								id: `class_${crypto.randomUUID()}`,
								label: 'New class',
								color: '#888888',
								opacity: 0.7,
								pricePerM2: 0,
							};
							const newThresholds = filter.thresholds.map(t => ({
								...t,
								boundaries: [
									...t.boundaries,
									(t.boundaries[t.boundaries.length - 1] || 500) + 200,
								],
							}));
							updateFilter({
								classes: [...filter.classes, newClass],
								thresholds: newThresholds,
							});
						}}>
						+ Add class
					</Button>
				</Section>
			</EditorColumn>

			<EditorColumn>
				{/* Threshold Matrix */}
				<Section>
					<SectionTitle>Threshold Matrix ({thresholdPropertyLabel})</SectionTitle>
					<Card>
						<Card.Body className="p-2">
							<MatrixTableWrapper>
								<MatrixTable size="sm" className="mb-0">
									<thead>
										<tr>
											<th>{bandPropertyLabel.split(' ')[0]}</th>
											<th>Min</th>
											{filter.classes.map(cls => (
												<th
													key={cls.id}
													style={{ background: `${cls.color}33` }}
													title={cls.label}>
													≤ {cls.label}
												</th>
											))}
											<th style={{ background: '#ff000022' }}>Max</th>
										</tr>
									</thead>
									<tbody>
										{filter.bands?.map((band, bi) => (
											<tr key={bi}>
												<td>
													<strong>{band}</strong>
												</td>
												<td>
													<input
														type="number"
														value={filter.thresholds[bi]?.min ?? ''}
														onChange={e => {
															const raw = e.target.value;
															const val =
																raw === '' ? null : Number(raw);
															const newThresholds =
																filter.thresholds.map((t, i) =>
																	i === bi
																		? { ...t, min: val }
																		: { ...t }
																);
															updateFilter({
																thresholds: newThresholds,
															});
														}}
													/>
												</td>
												{/* Each cell shows the upper boundary for this class at this band.
												    Fallback: if boundary doesn't exist yet, last class column
												    shows the band's max value, others show 0. */}
												{filter.classes.map((cls, ci) => (
													<td key={cls.id}>
														<input
															type="number"
															value={
																filter.thresholds[bi]?.boundaries?.[
																	ci
																] ?? ''
															}
															onChange={e => {
																const raw = e.target.value;
																const val =
																	raw === '' ? null : Number(raw);
																const newThresholds =
																	filter.thresholds.map(
																		(t, i) => {
																			if (i !== bi)
																				return {
																					...t,
																					boundaries: [
																						...(t.boundaries ||
																							[]),
																					],
																				};
																			const newBoundaries = [
																				...(t.boundaries ||
																					[]),
																			];
																			// Ensure boundaries array is long enough
																			while (
																				newBoundaries.length <=
																				ci
																			) {
																				newBoundaries.push(
																					0
																				);
																			}
																			newBoundaries[ci] = val;
																			return {
																				...t,
																				boundaries:
																					newBoundaries,
																			};
																		}
																	);
																updateFilter({
																	thresholds: newThresholds,
																});
															}}
														/>
													</td>
												))}
												<td>
													<input
														type="number"
														value={filter.thresholds[bi]?.max ?? ''}
														style={{ background: '#ff000011' }}
														onChange={e => {
															const raw = e.target.value;
															const val =
																raw === '' ? null : Number(raw);
															const newThresholds =
																filter.thresholds.map((t, i) =>
																	i === bi
																		? { ...t, max: val }
																		: { ...t }
																);
															updateFilter({
																thresholds: newThresholds,
															});
														}}
													/>
												</td>
											</tr>
										))}
									</tbody>
								</MatrixTable>
							</MatrixTableWrapper>
						</Card.Body>
					</Card>
					<small className="text-muted d-block mt-2">
						Each column shows the upper threshold for that class. Values above the last
						class threshold go to "Unclassified".
					</small>
				</Section>
			</EditorColumn>

			{/* Summary Column */}
			<EditorColumn>
				<Section>
					<SectionTitle>Cost Summary</SectionTitle>
					<SummaryCard>
						<Card.Body style={{ padding: '12px' }}>
							<TotalAreaRow>
								<span>Total area</span>
								<span>{areaTotal.toLocaleString()}</span>
							</TotalAreaRow>
							{filter.classes.map(cls => {
								const r = classificationResults[cls.id];
								const area = r?.totalArea || 0;
								const price = cls.pricePerM2 || 0;
								const cost = area * price;
								return (
									<SummaryRow key={cls.id}>
										<SummaryLabel>
											<ColorDot color={cls.color} />
											<span>
												{cls.label}
												<SummaryDetailText>
													{area.toLocaleString()} × {price.toFixed(2)}{' '}
													{currency.symbol}/m²
												</SummaryDetailText>
											</span>
										</SummaryLabel>
										<SummaryValue>
											{cost > 0
												? `${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency.symbol}`
												: '-'}
										</SummaryValue>
									</SummaryRow>
								);
							})}
							{classificationResults.below_minimum?.count > 0 && (
								<SummaryRow>
									<SummaryLabel>
										<ColorDot color="#808080" />
										Below Minimum
									</SummaryLabel>
									<SummaryValue>
										{classificationResults.below_minimum.count} (
										{classificationResults.below_minimum.areaPercentage}%)
									</SummaryValue>
								</SummaryRow>
							)}
							{classificationResults.unclassified?.count > 0 && (
								<SummaryRow>
									<SummaryLabel>
										<ColorDot color="#646464" />
										Unclassified
									</SummaryLabel>
									<SummaryValue>
										{classificationResults.unclassified.count} (
										{classificationResults.unclassified.areaPercentage}%)
									</SummaryValue>
								</SummaryRow>
							)}
							{/* Custom price fields — dynamic list of additional cost lines */}
							{customPriceFields.map((field, idx) => (
								<SummaryRow key={idx}>
									<SummaryLabel>
										<EditableLabelInput
											type="text"
											value={field.label}
											onChange={e =>
												updateCustomField(idx, { label: e.target.value })
											}
											aria-label={`Custom price label ${idx + 1}`}
											title="Click to edit label"
											maxLength={40}
										/>
									</SummaryLabel>
									<SummaryValue>
										<div className="d-flex align-items-center gap-1">
											<PriceInput
												type="number"
												step="0.01"
												value={field.price ?? ''}
												placeholder="0.00"
												aria-label={`Custom price per square meter ${idx + 1}`}
												title="Additional custom price per m² applied to total area"
												onChange={e => {
													const raw = e.target.value;
													updateCustomField(idx, {
														price: raw === '' ? 0 : parseFloat(raw),
													});
												}}
												style={priceInputStyle}
											/>
											<span
												className="text-muted"
												style={{ fontSize: '0.75rem' }}>
												{currency.symbol}/m²
											</span>
											<Button
												variant="link"
												className="text-danger p-0 ms-1"
												style={{ fontSize: '0.75rem', lineHeight: 1 }}
												title="Remove custom price"
												onClick={() => removeCustomField(idx)}>
												×
											</Button>
										</div>
									</SummaryValue>
								</SummaryRow>
							))}
							{customPriceFields.length > 0 && customFieldsTotalCost > 0 && (
								<SummaryRow>
									<SummaryLabel
										style={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
										Custom prices subtotal
									</SummaryLabel>
									<SummaryValue style={{ fontSize: '0.8rem' }}>
										{customFieldsTotalCost.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}{' '}
										{currency.symbol}
									</SummaryValue>
								</SummaryRow>
							)}
							<Button
								variant="link"
								size="sm"
								className="p-0 mt-2 mb-2"
								style={{ fontSize: '0.85rem', textDecoration: 'none' }}
								onClick={addCustomField}>
								+ Add custom price
							</Button>
							<TotalRow>
								<span>Total</span>
								<span>
									{grandTotal.toLocaleString(undefined, {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})}{' '}
									{currency.symbol}
								</span>
							</TotalRow>
						</Card.Body>
					</SummaryCard>
				</Section>

				<Section>
					<SectionTitle>Project Notes (optional)</SectionTitle>
					<Form.Control
						as="textarea"
						rows={3}
						placeholder="Add notes about the project, calculations, or observations..."
						value={projectNotes}
						onChange={e => setProjectNotes(e.target.value)}
						style={{ fontSize: '0.85rem' }}
					/>
					<small className="text-muted d-block mt-1">
						Notes will be included in exported reports.
					</small>
				</Section>

				<Button
					variant="success"
					className="w-100 mt-2"
					style={{ backgroundColor: '#14743B', borderColor: '#14743B' }}
					onClick={onOpenExportModal}
					disabled={isExporting}>
					{isExporting ? '⏳ Generating...' : '📄 Export Report'}
				</Button>
			</EditorColumn>
		</EditorGrid>
	);
});

export default MatrixEditor;
